// @ts-nocheck
// Agent Portal (/cti2) — one section frame for My Dashboard.
//
// The dashboard stacks four panels that were each built separately and bring
// mismatched chrome: AgentCallStatsPanel has its own toolbar card and an
// <h2>, LeadsScreen an <h4> with bootstrap-ish spacing, and both
// VoicemailsPanel and DispositionFollowUpsPanel return bare fragments with no
// container at all. Stacked with only bottom padding between them, nothing
// read as a section boundary.
//
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { HAIRLINE } from '../team/teamUi';

const SectionRefreshContext = createContext(null);
const REFRESH_COOLDOWN_MS = 1500;

export function useSectionRefresh(load) {
  const ctx = useContext(SectionRefreshContext);
  const loadRef = useRef(load);
  loadRef.current = load;
  const token = ctx?.token || 0;
  const seenRef = useRef(token);
  useEffect(() => {
    if (token === seenRef.current) return;
    seenRef.current = token;
    ctx.track(loadRef.current?.());
  }, [token]);
  return Boolean(ctx);
}

export default function DashboardSection({
  id,
  eyebrow,
  title,
  subtitle,
  right,
  framed = true,
  bodyClassName = '',
  onRefresh,
  refreshable = false,
  children,
}: any) {
  const showRefresh = refreshable || Boolean(onRefresh);
  const [token, setToken] = useState(0);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(0);

  const track = useCallback((p) => {
    if (!p || typeof p.then !== 'function') return;
    busyRef.current += 1;
    setBusy(true);
    const done = () => {
      busyRef.current -= 1;
      if (!busyRef.current) setBusy(false);
    };
    p.then(done, done);
  }, []);

  const lastClickRef = useRef(0);
  const refresh = () => {
    const now = Date.now();
    if (busyRef.current || now - lastClickRef.current < REFRESH_COOLDOWN_MS) return;
    lastClickRef.current = now;
    setToken((t) => t + 1);
    track(onRefresh?.());
  };

  const ctx = useMemo(() => (showRefresh ? { token, track } : null), [showRefresh, token, track]);

  return (
    <section id={id} style={{ scrollMarginTop: 'var(--section-scroll-offset, 56px)' }}>
      <div className="mb-2.5 flex items-end justify-between gap-4">
        <div className="min-w-0">
          {eyebrow ? (
            <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.11em] text-muted-foreground">
              {eyebrow}
            </div>
          ) : null}
          <h2 className="truncate text-[15px] font-semibold tracking-tight text-foreground">{title}</h2>
          {subtitle ? <p className="mt-0.5 truncate text-[11px] text-muted-foreground">{subtitle}</p> : null}
        </div>
        {right || showRefresh ? (
          <div className="flex shrink-0 items-center gap-2">
            {right}
            {showRefresh ? (
              <button
                type="button"
                onClick={refresh}
                disabled={busy}
                title={busy ? 'Refreshing...' : 'Refresh'}
                aria-label={`Refresh ${typeof title === 'string' ? title : 'section'}`}
                className="flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground transition-colors hover:text-foreground disabled:opacity-60"
              >
                <RefreshCw size={13} className={busy ? 'animate-spin' : ''} />
              </button>
            ) : null}
          </div>
        ) : null}
      </div>

      <div
        className={`${framed ? `cti-kpi-card card-hover-raise rounded-2xl border ${HAIRLINE} bg-card p-3` : ''} ${bodyClassName}`.trim()}
      >
        <SectionRefreshContext.Provider value={ctx}>{children}</SectionRefreshContext.Provider>
      </div>
    </section>
  );
}
